import React from 'react';
import { Button, CircularProgress, Paper, Snackbar, Alert, Tooltip } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useAtomValue, useSetAtom } from 'jotai';
import type { DeckGLRef } from '@deck.gl/react';
import type { MapRef } from 'react-map-gl/maplibre';
import {
  exportLegendDataAtom,
  viewStateAtom,
  exportModeAtom,
  exportChartCanvasAtom,
  exportStatsContainerRefAtom,
  statsPageContextAtom,
} from './atoms';
import {
  sanitizeFilename,
  loadImage,
  captureMapCanvas,
  renderBrandedExport,
  captureStatsPage,
  waitForPaintCycle,
  PAGE_WIDTH,
  PAGE_HEIGHT,
  PAGE_WIDTH_IN,
  PAGE_HEIGHT_IN,
} from './mapExportUtils';
import tempoLogoUrl from '../images/tempo_full_color_DKGray.svg?url';
import impactLogoUrl from '../images/IMPACT_DrkBlu_FULLCOLOR_ICON.svg?url';

type DownloadButtonProps = {
  title: string;
  /** Whether the map has finished loading and has canvases to export. */
  ready: boolean;
  deckRef: React.RefObject<DeckGLRef | null>;
  mapRef: React.RefObject<MapRef | null>;
};

export const DownloadButton = (props: DownloadButtonProps) => {
  const { title, ready, deckRef, mapRef } = props;
  const legendGroups = useAtomValue(exportLegendDataAtom);
  const viewState = useAtomValue(viewStateAtom);
  const statsContext = useAtomValue(statsPageContextAtom);
  const chartCanvases = useAtomValue(exportChartCanvasAtom);
  const statsContainerRef = useAtomValue(exportStatsContainerRefAtom);
  const setExportMode = useSetAtom(exportModeAtom);
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);

  const handleDownload = React.useCallback(async () => {
    if (!deckRef.current?.deck || !mapRef.current) {
      setExportError('Map is not ready. Please wait and try again.');
      return;
    }
    setIsExporting(true);
    setExportError(null);
    try {
      // Enable export mode for solid backgrounds, then wait for a full paint cycle
      setExportMode(true);
      await waitForPaintCycle();

      // Load logos once for both pages
      const [tempoLogo, impactLogo] = await Promise.all([
        loadImage(tempoLogoUrl),
        loadImage(impactLogoUrl),
      ]);

      // Page 1: branded map
      const mapCanvas = await captureMapCanvas(deckRef, mapRef);
      const bearing = viewState.bearing ?? 0;
      const page1Canvas = await renderBrandedExport(
        mapCanvas,
        title,
        legendGroups,
        bearing,
        tempoLogo,
        impactLogo
      );

      // Page 2: stats (if context available)
      let page2Canvas: HTMLCanvasElement | null = null;
      if (statsContext) {
        page2Canvas = await captureStatsPage(
          PAGE_WIDTH,
          PAGE_HEIGHT,
          title,
          tempoLogo,
          impactLogo,
          statsContext,
          chartCanvases,
          statsContainerRef
        );
      }

      // Assemble PDF - fixed US Letter landscape
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'in',
        format: 'letter',
      });

      doc.addImage(page1Canvas, 'PNG', 0, 0, PAGE_WIDTH_IN, PAGE_HEIGHT_IN);

      if (page2Canvas) {
        doc.addPage('letter', 'landscape');
        doc.addImage(page2Canvas, 'PNG', 0, 0, PAGE_WIDTH_IN, PAGE_HEIGHT_IN);
      }

      doc.save(`${sanitizeFilename(title) || 'export'}.pdf`);
    } catch (err: unknown) {
      console.error('Map download failed:', err);
      setExportError('PDF export failed. Please try again.');
    } finally {
      setExportMode(false);
      setIsExporting(false);
    }
  }, [
    title,
    deckRef,
    mapRef,
    legendGroups,
    viewState.bearing,
    statsContext,
    chartCanvases,
    statsContainerRef,
    setExportMode,
  ]);

  return (
    <>
      <Tooltip title="Download the map, its legend and charts as a PDF" describeChild>
        <Paper elevation={0}>
          <Button
            sx={{ backgroundColor: 'white', width: 30, minWidth: 30, height: 30 }}
            size="small"
            onClick={handleDownload}
            disabled={isExporting || !ready}
            aria-label="Download PDF"
          >
            {isExporting ? <CircularProgress size={18} /> : <Download />}
          </Button>
        </Paper>
      </Tooltip>
      <Snackbar
        open={exportError !== null}
        autoHideDuration={6000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      </Snackbar>
    </>
  );
};
